import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, RefreshCw, Trash2, X, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Contract } from "@/hooks/useContracts";

interface BulkContractActionsProps {
  selectedContracts: Contract[];
  onClearSelection: () => void;
}

export function BulkContractActions({ selectedContracts, onClearSelection }: BulkContractActionsProps) {
  const queryClient = useQueryClient();
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkEmail, setBulkEmail] = useState("");
  const [newStatus, setNewStatus] = useState<string>("");

  const bulkSendMutation = useMutation({
    mutationFn: async ({ contractIds, email }: { contractIds: string[]; email: string }) => {
      const updates = contractIds.map(id =>
        supabase
          .from("contracts")
          .update({
            status: "sent",
            sent_at: new Date().toISOString(),
            signer_email: email,
          })
          .eq("id", id)
      );
      
      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        throw new Error(`Failed to send ${errors.length} contracts`);
      }
      return results;
    },
    onSuccess: (_, { contractIds }) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success(`${contractIds.length} contracts sent for signature`);
      setSendDialogOpen(false);
      setBulkEmail("");
      onClearSelection();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ contractIds, status }: { contractIds: string[]; status: string }) => {
      const updateData: Record<string, any> = { status };
      if (status === "signed") {
        updateData.signed_at = new Date().toISOString();
      }
      
      const updates = contractIds.map(id =>
        supabase.from("contracts").update(updateData).eq("id", id)
      );
      
      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} contracts`);
      }
      return results;
    },
    onSuccess: (_, { contractIds, status }) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success(`${contractIds.length} contracts updated to ${status}`);
      setStatusDialogOpen(false);
      setNewStatus("");
      onClearSelection();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (contractIds: string[]) => {
      const deletes = contractIds.map(id =>
        supabase.from("contracts").delete().eq("id", id)
      );
      
      const results = await Promise.all(deletes);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        throw new Error(`Failed to delete ${errors.length} contracts`);
      }
      return results;
    },
    onSuccess: (_, contractIds) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success(`${contractIds.length} contracts deleted`);
      setDeleteDialogOpen(false);
      onClearSelection();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const draftContracts = selectedContracts.filter(c => c.status === "draft");
  const selectedIds = selectedContracts.map(c => c.id);

  if (selectedContracts.length === 0) return null;

  return (
    <>
      <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg mb-4">
        <Badge variant="secondary" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          {selectedContracts.length} selected
        </Badge>
        
        <div className="flex-1" />
        
        {draftContracts.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setSendDialogOpen(true)}
          >
            <Send className="h-4 w-4" />
            Send ({draftContracts.length})
          </Button>
        )}
        
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setStatusDialogOpen(true)}
        >
          <RefreshCw className="h-4 w-4" />
          Update Status
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-destructive hover:text-destructive"
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
        
        <Button variant="ghost" size="sm" onClick={onClearSelection}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Bulk Send Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send {draftContracts.length} Contracts</DialogTitle>
            <DialogDescription>
              All selected draft contracts will be sent to the same email address for signature.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contracts to send</Label>
              <div className="max-h-[120px] overflow-y-auto space-y-1">
                {draftContracts.map(c => (
                  <div key={c.id} className="text-sm p-2 bg-muted/50 rounded">
                    {c.title}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-email">Signer Email</Label>
              <Input
                id="bulk-email"
                type="email"
                placeholder="signer@example.com"
                value={bulkEmail}
                onChange={(e) => setBulkEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => bulkSendMutation.mutate({
                contractIds: draftContracts.map(c => c.id),
                email: bulkEmail,
              })}
              disabled={!bulkEmail || bulkSendMutation.isPending}
            >
              <Send className="h-4 w-4 mr-2" />
              Send All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status for {selectedContracts.length} Contracts</DialogTitle>
            <DialogDescription>
              Change the status of all selected contracts at once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="viewed">Viewed</SelectItem>
                  <SelectItem value="signed">Signed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => bulkStatusMutation.mutate({
                contractIds: selectedIds,
                status: newStatus,
              })}
              disabled={!newStatus || bulkStatusMutation.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Update All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedContracts.length} Contracts</DialogTitle>
            <DialogDescription>
              This action cannot be undone. All selected contracts will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {selectedContracts.map(c => (
                <div key={c.id} className="text-sm p-2 bg-destructive/10 rounded border border-destructive/20">
                  {c.title}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => bulkDeleteMutation.mutate(selectedIds)}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
